import { useState, useEffect } from 'react';
import { ref, get, set } from 'firebase/database';
import { HiOutlineUserCircle, HiOutlineMail } from 'react-icons/hi';
import { db } from '../firebase';

const DAILY_REPORT_EMAIL_RECIPIENTS_REF = 'DailyReportEmailRecipients';
const ANALYSIS_REFRESH_OFF = '0';
const ANALYSIS_REFRESH_OPTIONS = [ANALYSIS_REFRESH_OFF, '1', '5', '10', '15', '20', '25'];

function getSendDailyReportEmailRef(uid) {
  return uid ? `Users/${uid}/settings/sendDailyReportEmail` : null;
}

function getAnalysisRefreshMinutesRef(uid) {
  return uid ? `Users/${uid}/settings/analysisRefreshMinutes` : null;
}

function getDailyReportRecipientRef(uid) {
  return uid ? `${DAILY_REPORT_EMAIL_RECIPIENTS_REF}/${uid}` : null;
}

function Settings({ user, onSignOut }) {
  const [sendDailyReportEmail, setSendDailyReportEmail] = useState(false);
  const [sendDailyReportLoading, setSendDailyReportLoading] = useState(true);
  const [sendDailyReportError, setSendDailyReportError] = useState(null);
  const [sendDailyReportSaving, setSendDailyReportSaving] = useState(false);
  const [analysisRefreshMinutes, setAnalysisRefreshMinutes] = useState(ANALYSIS_REFRESH_OFF);
  const [analysisRefreshLoading, setAnalysisRefreshLoading] = useState(true);
  const [analysisRefreshError, setAnalysisRefreshError] = useState(null);
  const [analysisRefreshSaving, setAnalysisRefreshSaving] = useState(false);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setSendDailyReportLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setSendDailyReportError(null);
      try {
        const path = getSendDailyReportEmailRef(uid);
        if (!path) return;
        const snapshot = await get(ref(db, path));
        if (!cancelled) {
          const val = snapshot.val();
          setSendDailyReportEmail(val === true);
        }
      } catch (err) {
        if (!cancelled) {
          setSendDailyReportError(err.message || 'Failed to load setting');
        }
      } finally {
        if (!cancelled) setSendDailyReportLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const handleSendDailyReportToggle = async () => {
    const uid = user?.uid;
    if (!uid) return;
    const next = !sendDailyReportEmail;
    setSendDailyReportError(null);
    setSendDailyReportSaving(true);
    try {
      const settingsPath = getSendDailyReportEmailRef(uid);
      const recipientPath = getDailyReportRecipientRef(uid);
      if (!settingsPath || !recipientPath) return;
      await set(ref(db, settingsPath), next);
      if (next) {
        await set(ref(db, recipientPath), user.email ?? '');
      } else {
        await set(ref(db, recipientPath), null);
      }
      setSendDailyReportEmail(next);
    } catch (err) {
      setSendDailyReportError(err.message || 'Failed to update setting');
    } finally {
      setSendDailyReportSaving(false);
    }
  };

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setAnalysisRefreshLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setAnalysisRefreshError(null);
      try {
        const path = getAnalysisRefreshMinutesRef(uid);
        if (!path) return;
        const snapshot = await get(ref(db, path));
        const value = snapshot.val();
        if (!cancelled) {
          const str = typeof value === 'number' && Number.isInteger(value) && value >= 0 ? String(value) : ANALYSIS_REFRESH_OFF;
          setAnalysisRefreshMinutes(ANALYSIS_REFRESH_OPTIONS.includes(str) ? str : ANALYSIS_REFRESH_OFF);
        }
      } catch (err) {
        if (!cancelled) setAnalysisRefreshError(err.message || 'Failed to load analysis refresh setting');
      } finally {
        if (!cancelled) setAnalysisRefreshLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const handleAnalysisRefreshChange = async (e) => {
    const next = e.target.value;
    setAnalysisRefreshError(null);
    setAnalysisRefreshSaving(true);
    try {
      const path = getAnalysisRefreshMinutesRef(user?.uid);
      if (!path) return;
      const saveValue = next === ANALYSIS_REFRESH_OFF ? 0 : parseInt(next, 10);
      await set(ref(db, path), saveValue);
      setAnalysisRefreshMinutes(next);
    } catch (err) {
      setAnalysisRefreshError(err.message || 'Failed to update analysis auto-refresh');
    } finally {
      setAnalysisRefreshSaving(false);
    }
  };

  return (
    <div className="page page--full-width settings-page-wrap">
      <div className="settings-page">
        <header className="settings-header">
          <h1 className="settings-title">Settings</h1>
        </header>

        <div className="settings-groups">
        <section className="settings-group">
          <h2 className="settings-group-title">
            <HiOutlineMail className="settings-group-icon" aria-hidden />
            Notifications
          </h2>
          <ul className="settings-group-list">
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Send daily report email</span>
                <span className="settings-row-desc">Receive the daily report by email at your account address.</span>
                {user?.email && (
                  <span className="settings-row-email">Emails will be sent to: {user.email}</span>
                )}
              </div>
              <div className="settings-row-control">
                {sendDailyReportLoading ? (
                  <span className="settings-switch-loading">Loading…</span>
                ) : (
                  <label className="settings-switch">
                    <input
                      type="checkbox"
                      checked={sendDailyReportEmail}
                      onChange={handleSendDailyReportToggle}
                      disabled={sendDailyReportSaving}
                      className="settings-switch-input"
                      aria-describedby={sendDailyReportError ? 'send-daily-report-error' : undefined}
                    />
                    <span className="settings-switch-slider" />
                    <span className="settings-switch-label">{sendDailyReportEmail ? 'On' : 'Off'}</span>
                  </label>
                )}
              </div>
            </li>
            {sendDailyReportError && (
              <li className="settings-row settings-row--error" id="send-daily-report-error" role="alert">
                {sendDailyReportError}
              </li>
            )}
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Analysis page auto-refresh</span>
                <span className="settings-row-desc">Refresh Analysis data every N minutes.</span>
              </div>
              <div className="settings-row-control">
                {analysisRefreshLoading ? (
                  <span className="settings-switch-loading">Loading…</span>
                ) : (
                  <select
                    value={analysisRefreshMinutes}
                    onChange={handleAnalysisRefreshChange}
                    disabled={analysisRefreshSaving}
                    className="settings-select settings-select--inline"
                    aria-invalid={!!analysisRefreshError}
                  >
                    {ANALYSIS_REFRESH_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt === ANALYSIS_REFRESH_OFF ? 'Off' : `${opt} min`}</option>
                    ))}
                  </select>
                )}
              </div>
            </li>
            {analysisRefreshError && (
              <li className="settings-row settings-row--error" role="alert">{analysisRefreshError}</li>
            )}
          </ul>
        </section>

        <section className="settings-group">
          <h2 className="settings-group-title">
            <HiOutlineUserCircle className="settings-group-icon" aria-hidden />
            Account
          </h2>
          <ul className="settings-group-list">
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Sign out</span>
                <span className="settings-row-desc">Sign out of your account on this device.</span>
              </div>
              <div className="settings-row-control">
                <button type="button" className="btn btn-outline" onClick={onSignOut}>Sign out</button>
              </div>
            </li>
          </ul>
        </section>
        </div>
      </div>
    </div>
  );
}

export default Settings;
